// ============================================================
// TaskFlow OS - Constants & Configuration
// ============================================================

import type { NodeStatus, ApprovalStatus, PriorityLevel, WorkflowType } from './types';

// ---- Status Labels ----

export const NODE_STATUS_LABELS: Record<NodeStatus, string> = {
  pending: '未着手',
  in_progress: '進行中',
  completed: '完了',
  error: 'エラー',
  waiting: '待機中',
  cancelled: 'キャンセル',
};

export const APPROVAL_STATUS_LABELS: Record<ApprovalStatus, string> = {
  pending: '承認待ち',
  approved: '承認済み',
  rejected: '却下',
  revision_requested: '修正依頼',
};

export const PRIORITY_LABELS: Record<PriorityLevel, string> = {
  critical: '緊急',
  high: '高',
  medium: '中',
  low: '低',
};

export const WORKFLOW_TYPE_LABELS: Record<WorkflowType, string> = {
  operational: '実行ワークフロー',
  approval: '承認ワークフロー',
  design: '設計ワークフロー',
};

// ---- Status Colors ----

export const NODE_STATUS_COLORS: Record<NodeStatus, string> = {
  pending: '#f59e0b',
  in_progress: '#3b82f6',
  completed: '#10b981',
  error: '#ef4444',
  waiting: '#8b5cf6',
  cancelled: '#6b7280',
};

export const APPROVAL_STATUS_COLORS: Record<ApprovalStatus, string> = {
  pending: '#f59e0b',
  approved: '#10b981',
  rejected: '#ef4444',
  revision_requested: '#f97316',
};

export const PRIORITY_COLORS: Record<PriorityLevel, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#3b82f6',
  low: '#6b7280',
};

// ---- Node Animation Classes ----

export const NODE_STATUS_ANIMATIONS: Record<NodeStatus, string> = {
  pending: '',
  in_progress: 'animate-pulse-slow',
  completed: 'animate-glow-fade',
  error: 'animate-shake-error',
  waiting: 'animate-micro-glow',
  cancelled: 'opacity-60',
};

// ---- Role Labels ----

export const ROLE_LABELS: Record<string, string> = {
  admin: '管理者',
  manager: 'マネージャー',
  member: 'メンバー',
  viewer: '閲覧者',
};

// ---- Event Type Labels ----

export const EVENT_TYPE_LABELS: Record<string, string> = {
  node_created: 'ノード作成',
  node_updated: 'ノード更新',
  node_status_changed: 'ステータス変更',
  node_deleted: 'ノード削除',
  workflow_created: 'ワークフロー作成',
  workflow_updated: 'ワークフロー更新',
  workflow_deleted: 'ワークフロー削除',
  approval_requested: '承認リクエスト',
  approval_approved: '承認',
  approval_rejected: '却下',
  project_created: 'プロジェクト作成',
  project_updated: 'プロジェクト更新',
  project_archived: 'プロジェクトアーカイブ',
  member_added: 'メンバー追加',
  member_removed: 'メンバー削除',
  role_changed: 'ロール変更',
  comment_added: 'コメント追加',
  file_uploaded: 'ファイルアップロード',
  plugin_executed: 'プラグイン実行',
  ai_suggestion: 'AI提案',
  ai_analysis: 'AI分析',
  system_event: 'システムイベント',
};

// ---- Event Type Icons (SVG icon names) ----

export const EVENT_TYPE_ICONS: Record<string, string> = {
  node_created: 'plus-circle',
  node_updated: 'edit',
  node_status_changed: 'refresh',
  node_deleted: 'trash',
  workflow_created: 'git-branch',
  workflow_updated: 'git-commit',
  workflow_deleted: 'git-branch',
  approval_requested: 'clock',
  approval_approved: 'check-circle',
  approval_rejected: 'x-circle',
  project_created: 'folder-plus',
  project_updated: 'folder',
  project_archived: 'archive',
  member_added: 'user-plus',
  member_removed: 'user-minus',
  role_changed: 'shield',
  comment_added: 'message-circle',
  file_uploaded: 'upload',
  plugin_executed: 'zap',
  ai_suggestion: 'cpu',
  ai_analysis: 'bar-chart',
  system_event: 'settings',
};

// ---- Default Layout ----

export const DEFAULT_NODE_WIDTH = 280;
export const DEFAULT_NODE_HEIGHT = 120;
export const NODE_SPACING_X = 350;
export const NODE_SPACING_Y = 180;
